import { useCallback, useEffect, useMemo } from 'react';

import { FieldPath, FieldPathValue, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useVoiceSTT } from '@/hooks/useVoiceProviders';
import { VoiceIntegration } from '@/shared/types/integration';

import { STTDynamicSettings } from './STTDynamicSettings';
import { STTModelSelector } from './STTModelSelector';
import CardBase from '../../Components/CardBase';
import CardSkeleton from '../../Components/CardSkeleton';

/**
 * Transcription configuration card for voice integrations
 *
 * Provides a multi-provider STT configuration with:
 * - Single model selector showing all models from all providers
 * - Provider is auto-derived from the selected model
 * - Dynamic settings (dependent on selected model)
 */
const Transcription = () => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<VoiceIntegration>();

  // Fetch STT providers and models from API
  const { models, isLoading, getModelById } = useVoiceSTT();

  // Watch the current STT configuration values
  // Model comes from the provider-specific config
  const sttProvider = useWatch<VoiceIntegration, 'config.stt_provider'>({
    name: 'config.stt_provider',
  });
  const deepgramModel = useWatch<VoiceIntegration, 'config.stt_deepgram.model'>({
    name: 'config.stt_deepgram.model',
  });
  const elevenlabsModel = useWatch<VoiceIntegration, 'config.stt_elevenlabs.model_id'>({
    name: 'config.stt_elevenlabs.model_id',
  });
  const sonioxModel = useWatch<VoiceIntegration, 'config.stt_soniox.model'>({
    name: 'config.stt_soniox.model',
  });
  // Language is synced with TTS config
  const language = useWatch<VoiceIntegration, 'config.tts_elevenlabs.language_code'>({
    name: 'config.tts_elevenlabs.language_code',
  });

  // Derive current model based on provider
  const getModelForProvider = (provider: string) => {
    switch (provider) {
      case 'deepgram':
        return deepgramModel;
      case 'elevenlabs':
        return elevenlabsModel;
      default:
        return sonioxModel;
    }
  };
  const model = getModelForProvider(sttProvider);

  // Set default model if none exists in the form (only for new integrations)
  useEffect(() => {
    if (!getValues('config.stt_provider')) {
      setValue('config.stt_provider', 'soniox', { shouldDirty: false });
      setValue('config.stt_soniox.model', 'stt-rt-v4', { shouldDirty: false });
    }
  }, [getValues, setValue]);

  // Get the base path for settings based on provider
  // Each provider stores settings in its own config object
  const getSettingsBasePath = useCallback((provider: string) => {
    switch (provider) {
      case 'deepgram':
        return 'config.stt_deepgram';
      case 'elevenlabs':
        return 'config.stt_elevenlabs';
      default:
        return 'config.stt_soniox';
    }
  }, []);

  // Handle model change - also updates the provider based on selected model
  // Each provider's settings are stored in their own config object (stt_deepgram, stt_soniox)
  const handleModelChange = useCallback(
    (newModel: string) => {
      const modelInfo = getModelById(newModel);
      const newProvider = modelInfo?.provider ?? 'soniox';
      const basePath = getSettingsBasePath(newProvider);

      setValue('config.stt_provider', newProvider, { shouldDirty: true });

      // Update model in the provider-specific config
      if (newProvider === 'deepgram') {
        setValue(
          'config.stt_deepgram.model',
          newModel as VoiceIntegration['config']['stt_deepgram']['model'],
          { shouldDirty: true }
        );
      } else if (newProvider === 'elevenlabs') {
        setValue('config.stt_elevenlabs.model_id', newModel, { shouldDirty: true });
      } else {
        setValue('config.stt_soniox.model', newModel, { shouldDirty: true });
      }

      // Apply default values for unset fields when switching models
      if (model !== newModel && modelInfo?.settings) {
        modelInfo.settings.forEach((setting) => {
          const fieldPath = `${basePath}.${setting.key}` as FieldPath<VoiceIntegration>;
          const currentValue = getValues(fieldPath);
          // Only apply default if the current value is undefined
          if (currentValue === undefined && setting.default !== undefined) {
            setValue(
              fieldPath,
              setting.default as FieldPathValue<VoiceIntegration, typeof fieldPath>,
              { shouldDirty: true }
            );
          }
        });
      }
    },
    [getModelById, getValues, setValue, getSettingsBasePath, model]
  );

  // Fallback to a compatible model when language changes and current model doesn't support it
  useEffect(() => {
    if (!models || models.length === 0 || !model) {
      return;
    }

    // Filter models that support the current language
    const compatibleModels = language
      ? models.filter((m) => m.supportedLanguages.includes(language))
      : models;

    // Check if current model is compatible with the selected language
    const isCurrentModelCompatible = compatibleModels.some((m) => m.id === model);

    // If current model is not compatible, fallback to first compatible model
    if (!isCurrentModelCompatible && compatibleModels.length > 0) {
      handleModelChange(compatibleModels[0].id);
    }
  }, [language, model, models, handleModelChange]);

  const allModels = useMemo(() => models ?? [], [models]);
  const languageFilteredModels = useMemo(
    () => (language ? allModels.filter((m) => m.supportedLanguages.includes(language)) : allModels),
    [allModels, language]
  );

  const effectiveModel = useMemo(() => {
    if (model) {
      const isModelInAllModels = allModels.some((m) => m.id === model);
      const isModelInFilteredModels = languageFilteredModels.some((m) => m.id === model);
      if (isModelInFilteredModels) {
        return model;
      }
      if (isModelInAllModels) {
        return model;
      }
    }
    return languageFilteredModels[0]?.id ?? allModels[0]?.id ?? '';
  }, [model, allModels, languageFilteredModels]);

  // Get current model info for dynamic settings
  const currentModelInfo = getModelById(effectiveModel);

  // Determine the base path for settings based on the current provider
  const currentProvider = currentModelInfo?.provider ?? 'soniox';
  const settingsBasePath = getSettingsBasePath(currentProvider);

  if (isLoading) {
    return (
      <CardSkeleton title={t('integrations.voice_common.transcription.speech_to_text')} rows={2} />
    );
  }

  return (
    <CardBase title={t('integrations.voice_common.transcription.speech_to_text')}>
      <STTModelSelector
        value={effectiveModel}
        onChange={handleModelChange}
        models={allModels}
        language={language}
        disabled={isLoading}
      />

      {currentModelInfo && (
        <STTDynamicSettings modelInfo={currentModelInfo} basePath={settingsBasePath} />
      )}
    </CardBase>
  );
};

export default Transcription;
